/**
 * 매니저 통계 페이지 — 학생을 고르지 않았을 때 오른쪽 칸(설계 §3-1 · §3-2). 학원 전체.
 *
 * 캘린더 → 현재·이달 현황(ST1)과 기간별 비율 → 통계 요약. 요약을 고르면 별도 주소의 상세 페이지로 이동한다.
 * 「열린 문의」 카드는 뺐다 — 문의 기능이 없어 서버가 늘 null 을 준다(백엔드 2단계 전달 §4-F).
 * 현황 탭은 기준일 인원(ST2) 상자 둘 — 수강 기간(ST5) · 확인 필요(ST8)는 만들지 않는다(오너 결정 2026-10-13, 백엔드 6단계 전달 §1).
 *
 * 현황과 비율은 오른쪽 칸 704px부터 나란히 놓고, 그래프는 실제 남은 폭에 맞춰 단을 바꾼다.
 */
import React, { useEffect, useState } from 'react';
import { AcademyButton, DateInput } from './controls';
import { ArrowLeft, Settings2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../common';
import { getStatsSummary, isPredeployError } from './academyApi';
import { activeHeadcount, formatRate, withdrawalStats } from './academyModel';
import { addDays, todayKST } from './periodDates';
import { Box, ErrorNotice, useAcademyVersion, useAsync, useElementWidth } from './ui';
import OverviewTab from './stats/OverviewTab';
import CalendarPanel from './stats/CalendarPanel';
import { ADMISSION_PERIODS, admissionPeriodRange, validDate } from './stats/reportModel';
import { FlowContent, NotesPreview } from './stats/ReportContent';
import ScrollRegion from './stats/ScrollRegion';
import { AdmissionsSummary } from './stats/AdmissionsReport';
import './StatsPanel.css';

/** 상자를 두 단으로 펴는 폭 */
const TWO_COL_MIN = 560;

function SummaryRow({ label, displayLabel = label, value, sub, loading, onClick, subAction, unit = '명' }) {
  const clickableRow = onClick && !subAction;
  const Tag = clickableRow ? 'button' : 'div';
  const valueText = Number.isFinite(value) ? `${value.toLocaleString('ko-KR')}${unit}` : value ?? '—';
  return <Tag {...(clickableRow ? { type: 'button', onClick, 'aria-label': `${label} 자세히` } : {})}
    className={`grid min-h-10 min-w-0 grid-cols-[4rem_minmax(0,1fr)] items-center gap-x-2 border-b border-gray-100 px-1 py-0.5 text-left last:border-b-0 ${clickableRow ? 'rounded-lg transition-colors hover:bg-primary/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary' : ''}`}>
    <span className="text-xs text-gray-500">{displayLabel}</span>
    <span className="min-w-0">
      {loading ? <span className="block h-6 w-12 animate-pulse rounded bg-gray-100" />
        : subAction && onClick ? <button type="button" aria-label={`${label} 자세히`} onClick={onClick} className="block text-left text-lg font-bold tabular-nums leading-5 text-gray-900 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary">{valueText}</button>
          : <span className="block text-lg font-bold tabular-nums leading-5 text-gray-900">{valueText}</span>}
      {subAction && sub && <span className="block text-xs leading-4 text-gray-500">{sub}</span>}
      {subAction ? (subAction.onClick
        ? <button type="button" aria-label={subAction.label} onClick={subAction.onClick} className="block min-h-6 text-left text-xs leading-4 text-danger hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary">{loading ? '\u00a0' : subAction.text}</button>
        : <span className="block min-h-6 text-xs leading-4 text-danger">{loading ? '\u00a0' : subAction.text}</span>)
        : <span className="block text-xs leading-4 text-gray-500">{loading ? '\u00a0' : sub || '\u00a0'}</span>}
    </span>
  </Tag>;
}

function SummaryList({ s, onOpenReport, today }) {
  if (s.error) return <ErrorNotice error={s.error} onRetry={s.reload} compact />;
  const d = s.data;
  const loading = s.loading && !d;
  const m = d?.month || {};
  const withdrawal = withdrawalStats(m.activeClosing, m.stopped);
  const go = (report, values) => onOpenReport ? () => onOpenReport(report, values) : undefined;
  const flowRange = { from: `${today.slice(0, 7)}-01`, to: today, bucket: 'month' };
  return (
    <div className="academy-summary-list grid grid-cols-1">
      {/* 재원은 휴원을 뺀 수(오너 2026-10-10) — 서버 인원(headcount)은 휴원을 넣은 수라 뺀다(요청서 D6) */}
      <SummaryRow loading={loading} label="재원" value={activeHeadcount(d?.headcount, d?.paused)} sub={`휴원 ${d?.paused ?? 0}명 별도`} onClick={go('headcount', { as_of: today, by: 'grade' })} />
      <SummaryRow loading={loading} label="이번 달 신규" displayLabel="이달 신규" value={d ? (m.registeredNew ?? 0) + (m.registeredAgain ?? 0) : null} sub={`재등록 ${m.registeredAgain ?? 0} · 예정 ${m.scheduledRegistered ?? 0}`} onClick={go('flows', flowRange)} />
      <SummaryRow loading={loading} label="이번 달 퇴원" displayLabel="이달 퇴원" value={m.stopped} unit="건" sub={`휴원 ${m.stoppedPause ?? '—'} · 일반 퇴원 ${m.stoppedWithdraw ?? '—'}`} onClick={go('flows', flowRange)}
        subAction={{ label: '30일 안 퇴원 예정 자세히', text: `30일 내 예정 ${d?.withdrawScheduled30 ?? '—'}명`, onClick: go('withdrawals', { from: today, to: addDays(today, 29) }) }} />
      <SummaryRow loading={loading} label="이번 달 퇴원율" displayLabel="이달 퇴원율" value={formatRate(withdrawal.rate)} sub={withdrawal.total != null ? `휴원 포함 · 중단 ${m.stopped}건` : ''} onClick={go('flows', flowRange)} />
    </div>
  );
}

function StatsPanel({ onOpenClasses, onBack, filters = {}, onFiltersChange = () => {}, onOpenStudent, onOpenNotes, onOpenReport, scrollKey = 'academy-overview' }) {
  const { getAccessToken } = useAuth();
  const version = useAcademyVersion();
  const today = todayKST();
  // 기준일 칸에 보이는 글과 통계가 쓰는 날을 나눈다 — 고쳐 쓰는 중(덜 적은 날짜)에는 칸이 ''를 보내는데, 그때 오늘로 되돌리면
  // 칸이 적던 글을 오늘로 덮는다. 온전한 날짜가 왔을 때만 통계를 그날로 옮긴다
  const initialDate = validDate(filters.as_of) && filters.as_of <= today ? filters.as_of : today;
  const [asOfInput, setAsOfInput] = useState(initialDate);
  const [asOf, setAsOf] = useState(initialDate);
  useEffect(() => { setAsOfInput(initialDate); setAsOf(initialDate); }, [initialDate]);
  // 숫자 띠(ST1)는 늘 오늘 기준 — 이 응답으로 서버 미배포도 가른다(설계 §8: 오른쪽 칸에 안내 한 번)
  const summary = useAsync((signal) => getStatsSummary({ signal }, getAccessToken), `summary-${version}`);
  const predeploy = isPredeployError(summary.error);
  const [ref, width] = useElementWidth();
  const monthFrom = `${today.slice(0, 7)}-01`;
  const rateRange = admissionPeriodRange(filters.admission_period, today);
  const more = (label, report, values = {}) => onOpenReport && <button type="button" aria-label={`${label} 자세히`} className="inline-flex min-h-6 items-center text-xs text-blue-700 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary" onClick={() => onOpenReport(report, values)}>자세히</button>;

  return (
    <div ref={ref} className="flex size-full flex-col overflow-hidden rounded-3xl bg-white shadow-lg">
      <div className="flex min-w-0 shrink-0 items-center gap-2 border-b px-3 py-2">
        {onBack && (
          <Button variant="ghost" size="sm" onClick={onBack} aria-label="목록으로" className="flex shrink-0 items-center gap-1">
            <ArrowLeft className="size-4" />
          </Button>
        )}
        <h3 className="shrink-0 text-base font-semibold text-gray-900">학원 현황</h3>
        <span className="min-w-0 truncate text-xs text-gray-500">{today} 기준 · 매니저</span>
        {!predeploy && onOpenClasses && (
          <AcademyButton type="button" onClick={onOpenClasses} className="ml-auto">
            <Settings2 className="size-4" />반 · 기준 설정
          </AcademyButton>
        )}
      </div>
      <ScrollRegion scrollKey={scrollKey} compact preservePositionOnChange>
        {predeploy && <ErrorNotice error={summary.error} />}
        {!predeploy && <CalendarPanel
          filters={{ month: filters.cal_month, day: filters.cal_day, kind: filters.cal_kind, view: filters.cal_view }}
          onChange={(changes) => onFiltersChange(Object.fromEntries(Object.entries(changes).map(([key, value]) => [`cal_${key}`, value])))}
          onOpenStudent={onOpenStudent}
          onOpenNotes={onOpenNotes}
        />}
        {!predeploy && <div className="academy-summary min-w-0 rounded-2xl border border-gray-200"><div className="academy-summary-layout grid min-w-0 grid-cols-1">
          <section aria-label="현재 · 이달 현황" className="academy-summary-current min-w-0 border-b border-gray-200 p-3">
            <h4 className="mb-2 flex min-h-6 items-center text-sm font-semibold text-gray-900">현재 · 이달 현황</h4>
            <SummaryList s={summary} onOpenReport={onOpenReport} today={today} />
          </section>
          <section className="academy-summary-rates flex min-w-0 flex-col p-3">
            <div className="mb-2 flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
              <h4 className="shrink-0 text-sm font-semibold text-gray-900">입학률·퇴원율</h4>
              <div role="group" aria-label="입학률·퇴원율 기간" className="ml-auto flex flex-wrap gap-1">
                {ADMISSION_PERIODS.map(([key, label]) => <button key={key} type="button" aria-pressed={rateRange.period === key} onClick={() => onFiltersChange({ admission_period: key })}
                  className={`min-h-6 rounded-full px-2 py-0.5 text-xs focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${rateRange.period === key ? 'bg-primary font-medium text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>{label}</button>)}
              </div>
            </div>
            <div className="academy-summary-charts grid flex-1 grid-cols-1 gap-3">
              <div className="flex min-w-0 flex-col justify-center"><AdmissionsSummary period={rateRange.period} onOpenReport={onOpenReport} /></div>
              <div className="academy-summary-withdrawal flex min-w-0 flex-col justify-center border-t border-gray-100 pt-3">
                <FlowContent from={rateRange.from} to={rateRange.to} preview summaryAction={more('입퇴원 통계', 'flows', { from: rateRange.from, to: rateRange.to, bucket: 'month' })} />
              </div>
            </div>
          </section>
        </div></div>}
        {!predeploy && <div className="flex flex-wrap items-center gap-2">
          <h4 className="text-sm font-semibold text-gray-900">재원 통계</h4>
          <label className="ml-auto flex items-center gap-2 text-xs text-gray-500">
            기준일
            <DateInput
              value={asOfInput}
              max={today}
              onChange={(v) => { setAsOfInput(v); if (validDate(v) && v <= today) { setAsOf(v); onFiltersChange({ as_of: v }); } }}
              compact
              fullWidth={false}
            />
          </label>
        </div>}
        {!predeploy && <OverviewTab asOf={asOf} wide={width >= TWO_COL_MIN} onOpenReport={onOpenReport} schoolLevel={filters.school_level} onSchoolLevelChange={(level) => onFiltersChange({ school_level: level })} />}
        {!predeploy && <div className={`grid gap-2 ${width >= TWO_COL_MIN ? 'grid-cols-2' : 'grid-cols-1'}`}>
          <Box compact title="반·강사 통계" sub="오늘 수강 · 이달 입·퇴반" right={more('반·강사 통계', 'groups', { from: monthFrom, to: today, bucket: 'month', group: 'class' })}><FlowContent from={monthFrom} to={today} groupBy="class" preview /></Box>
          <Box compact title="학원·반 메모" sub="고정 먼저 · 최대 3건" right={more('메모', 'notes')}><NotesPreview onOpenNotes={onOpenNotes} /></Box>
        </div>}
      </ScrollRegion>
    </div>
  );
}

export default StatsPanel;
